import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { codePattern } from '@/constants/validation';
import { Box, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectPhoneNumber,
  setPhoneNumber,
  setSurveyIsCompleted,
} from '@/store/reducers/survey/surveySlice';
import {
  selectError,
  selectIsAccountCreating,
  selectIsLoading,
  selectIsUserFromMobile,
  setError,
} from '@/store/reducers/user/userSlice';
import { confirmOTPCode, requestOTPCode } from '@/store/reducers/user/actions';
import { ReactComponent as Attention } from '@/assets/images/attention.svg';
import { disablePhoneAutoLink } from '@/utils/formats';
import { redirectToApp } from '@/utils/appRedirect';
import { formatError } from '@/utils/error';
import { match } from '@/utils/matcher';
import VerificationInput from 'react-verification-input';
import AuthForm from '../forms/AuthForm';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';
import analytics from '@/utils/analytics/analyticsInstance';
import cn from 'classnames';
import { ROUTES } from '@/constants/routes';
import { EXCEPTION_CODES } from '@/api/exceptions/codes';

type FormDataType = {
  code: string;
};

const ConfirmCodeStep: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isFromMobile = useAppSelector(selectIsUserFromMobile);
  const phoneNumber = useAppSelector(selectPhoneNumber);
  const isConfirmLoading = useAppSelector(selectIsAccountCreating);
  const isResendLoading = useAppSelector(selectIsLoading);
  const error = useAppSelector(selectError);
  const [timer, setTimer] = useState(60);
  const { palette } = useTheme();
  const { next, back, goToBankStep } = useSurveyNavigate();
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<FormDataType>({
    mode: 'onChange',
    defaultValues: { code: '' },
  });

  const actionButtonLabel = useMemo(
    () =>
      match<boolean, string>(true)
        .when(
          () => isValid,
          () => 'Continue',
        )
        .when(
          () => timer === 0,
          () => 'Resend code',
        )
        .otherwise(() => `Resend in ${timer}s`),
    [isValid, timer],
  );

  const handleFocus = useCallback(() => {
    analytics.track('authOTPInputSelected', {
      place: isFromMobile ? 'mobile' : 'web',
    });
  }, [isFromMobile]);

  const onSubmit = async (data: FormDataType) => {
    const place = isFromMobile ? 'mobile' : 'web';
    dispatch(setError(null));

    await analytics.track('authSignUpClicked', { place });
    await dispatch(
      confirmOTPCode({
        code: data.code,
        phoneNumber,
        context: { location: location.pathname, place },
        onSuccess: (accessToken, isLoginRequired) => {
          dispatch(setPhoneNumber(''));

          if (isLoginRequired) {
            goToBankStep();

            return;
          }

          if (isFromMobile) redirectToApp(accessToken);
          else next();
        },
        onError: ({ code }) => {
          if (code === EXCEPTION_CODES.SURVEYANSWER_NOT_UNIQUE) {
            navigate(ROUTES.DOWNLOAD_APP);
            dispatch(setSurveyIsCompleted(true));
          }
        },
      }),
    );
  };

  const handleResend = async () => {
    const place = isFromMobile ? 'mobile' : 'web';

    await analytics.track('authOTPResendClicked', { place });
    await dispatch(requestOTPCode({ phoneNumber, context: { place } }));

    setTimer(60);
  };

  useEffect(() => {
    dispatch(setError(null));
    analytics.track('webOnboardingScreenOpened', { screen_title: 'Confirm code' });
  }, [dispatch]);

  useEffect(() => {
    if (timer === 0) return;

    const interval = setInterval(() => {
      setTimer((t) => t - 1);
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [timer]);

  return (
    <AuthForm<FormDataType>
      title={`Enter the code we sent to \n ${disablePhoneAutoLink(phoneNumber)}`}
      input={{
        control,
        name: 'code',
        rules: {
          required: {
            value: true,
            message: 'This field is required',
          },
          pattern: {
            value: codePattern,
            message: 'Code must be exactly 4 digits',
          },
        },
        render: ({ field: { value, onChange } }) => (
          <Box gap={2} display="flex" flexDirection="column" alignItems="center">
            <VerificationInput
              autoFocus
              value={value}
              onChange={(value) => {
                onChange(value);
                dispatch(setError(null));
              }}
              onFocus={handleFocus}
              length={4}
              placeholder="-"
              validChars="0-9"
              classNames={{
                container: 'code-container',
                character: cn('code-character', { 'code-error': error }),
              }}
              inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code' }}
            />
            {error && (
              <Box gap={0.5} display="flex" alignItems="center">
                <Attention />
                <Typography color="error" variant="subtitle2">
                  {formatError('code')(error)}
                </Typography>
              </Box>
            )}
          </Box>
        ),
      }}
      buttons={[
        {
          id: 'change',
          name: 'Change the phone number',
          onClick: back,
          style: {
            backgroundColor: 'transparent',
            color: palette.border.contrastText,
            textTransform: 'initial',
          },
        },
        {
          id: 'submit',
          name: actionButtonLabel,
          loading: isConfirmLoading || isResendLoading,
          disabled: (!isValid && timer !== 0) || isConfirmLoading || isResendLoading,
          onClick: isValid ? handleSubmit(onSubmit) : handleResend,
        },
      ]}
    />
  );
};

export default ConfirmCodeStep;
